'use client';

import { useEffect, useState } from 'react';
import { T } from '@/lib/tokens';

export type OrbState = 'idle' | 'speaking' | 'listening' | 'thinking';

function useFakeAudioLevel(active: boolean): number {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!active) {
      setLevel(0);
      return;
    }
    let raf: number;
    let t = 0;
    const tick = () => {
      t += 0.05;
      const base =
        0.45 +
        Math.sin(t * 1.7) * 0.18 +
        Math.sin(t * 4.3 + 1.2) * 0.12 +
        (Math.random() - 0.5) * 0.08;
      setLevel(Math.max(0.05, Math.min(1, base)));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return level;
}

interface OrbProps {
  state?: OrbState;
  size?: number;
  /** Real microphone level (0-1) from the caller — the participant's voice.
   *  Drives the equatorial rings (the design's `listenLevel`). When omitted
   *  in the listening state a simulated signal is used instead. */
  micLevel?: number;
  /** Real AI speaker level (0-1) — the AI's audio output. Drives the terra
   *  halo (the design's `speakLevel`). When omitted in the speaking state a
   *  simulated signal is used instead. */
  aiLevel?: number;
}

export function Orb({ state = 'idle', size = 240, micLevel, aiLevel }: OrbProps) {
  const speaking = state === 'speaking';
  const listening = state === 'listening';
  const thinking = state === 'thinking';

  // Two independent audio-reactive signals that can coexist during the
  // speaking→listening transition. Fall back to a simulated signal only
  // when a real level isn't supplied by the caller.
  const fakeSpeak = useFakeAudioLevel(speaking && aiLevel === undefined);
  const fakeListen = useFakeAudioLevel(listening && micLevel === undefined);

  // AI speaking → terra halo (`speakLevel`)
  const speakLevel = speaking ? aiLevel ?? fakeSpeak : 0;
  // Participant voice → equatorial rings (`listenLevel`). Driven by the REAL mic
  // level whenever it's supplied — NOT just in the 'listening' state — so a reply
  // during the post-AI window (before the orb flips to listening) still shows the
  // voice is being heard. Falls back to a simulated signal only when no real level
  // is provided (e.g. test pages).
  const listenLevel = micLevel !== undefined ? micLevel : (listening ? fakeListen : 0);

  // Terra halo intensity. At rest it holds a low ambient value (0.14) rather
  // than going dark, keeping the orb warm between turns.
  const terraGlow = speaking ? 0.55 + speakLevel * 0.4 : 0.14;
  // Outer halo expands spatially with volume while speaking, not just brightness.
  const outerInset = speaking
    ? -(size * 0.2 + speakLevel * size * 0.22) // -0.2*size (quiet) → -0.42*size (loud)
    : -size * 0.25;

  // Equatorial ring drive. Rings appear whenever the participant is actually making
  // sound — above a small floor so ambient room noise doesn't light them — regardless
  // of orb state. When no real mic level is supplied, fall back to the listening state.
  const ringsActive = micLevel !== undefined ? micLevel > 0.06 : listening;
  const listenGlow = ringsActive ? 0.35 + listenLevel * 0.45 : 0;

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {/* ── Layer 1: Terra/orange halo — AI speaking only ──
          Diffuse radial glow that expands spatially with the AI's voice.
          Fast/reactive transitions while speaking; slow fade-out at rest. */}
      <div
        style={{
          position: 'absolute',
          inset: outerInset,
          background: `radial-gradient(circle, oklch(78% 0.13 48 / ${(terraGlow * 0.5).toFixed(3)}) 0%, transparent 65%)`,
          filter: 'blur(18px)',
          transition: speaking ? 'background 0.08s, inset 0.08s' : 'background 0.35s',
          pointerEvents: 'none',
        }}
      />
      {/* Mid halo */}
      <div
        style={{
          position: 'absolute',
          inset: -size * 0.1,
          background: `radial-gradient(circle, oklch(72% 0.14 48 / ${terraGlow.toFixed(3)}) 0%, transparent 60%)`,
          filter: 'blur(8px)',
          transition: speaking ? 'background 0.08s' : 'background 0.2s',
          pointerEvents: 'none',
        }}
      />

      {/* ── Layer 2: Equatorial rings — participant voice (listening) only ──
          4 concentric rings. Inner ring appears immediately when listening starts
          (threshold 0). Outer rings fade in progressively as volume rises.
          Two signals respond simultaneously: ring SIZE expands outward, and ring
          STROKE WIDTH grows with volume — the thickness change is the strongest
          "I can hear you" cue. Border-box compensation keeps each ring's visual
          center at a consistent radius regardless of stroke thickness. */}
      {[
        { s: 0.80, threshold: 0.00, maxOp: 0.88, baseW: 1.5, maxW: 4.5 },
        { s: 0.94, threshold: 0.28, maxOp: 0.58, baseW: 1.2, maxW: 3.0 },
        { s: 1.08, threshold: 0.52, maxOp: 0.38, baseW: 0.9, maxW: 2.0 },
        { s: 1.22, threshold: 0.65, maxOp: 0.22, baseW: 0.7, maxW: 1.5 },
      ].map(({ s, threshold, maxOp, baseW, maxW }, i) => {
        const ringOp = Math.min(1, Math.max(0, (listenGlow - threshold) * 2.4)) * maxOp;
        const strokeW = baseW + listenLevel * (maxW - baseW);
        // Border-box compensation: CSS border grows inward, shifting the visual
        // center of the stroke toward the orb as strokeW increases.
        // Add (strokeW - baseW) to the diameter so the center stays fixed.
        const baseRingSize = size * (s + listenLevel * 0.08);
        const ringSize = baseRingSize + (strokeW - baseW);
        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: ringSize,
              height: ringSize,
              borderRadius: '50%',
              border: `${strokeW}px solid oklch(70% 0.11 55 / 0.88)`,
              boxShadow: `0 0 ${5 + i * 3.5}px oklch(72% 0.10 55 / 0.33)`,
              filter: `blur(${(1.6 + i * 0.9).toFixed(2)}px)`,
              opacity: ringOp,
              transition: 'opacity 0.18s ease-out, width 0.1s ease-out, height 0.1s ease-out, border-width 0.08s ease-out',
              pointerEvents: 'none',
            }}
          />
        );
      })}

      {/* Orb body — breathing animation via CSS keyframes in globals.css */}
      <div
        style={{
          width: size * 0.7,
          height: size * 0.7,
          borderRadius: '50%',
          background:
            'radial-gradient(circle at 35% 30%, oklch(82% 0.10 55), oklch(62% 0.13 48) 55%, oklch(48% 0.14 45) 100%)',
          boxShadow: `
            inset -8px -16px 32px oklch(40% 0.13 45 / 0.6),
            inset 6px 8px 24px oklch(94% 0.04 65 / 0.5),
            0 12px 40px oklch(48% 0.14 45 / 0.35)
          `,
          position: 'relative',
          animation: thinking
            ? 'orbBreathe 1.4s ease-in-out infinite'
            : 'orbBreathe 4s ease-in-out infinite',
        }}
      >
        {/* Specular highlight */}
        <div
          style={{
            position: 'absolute',
            top: '12%',
            left: '20%',
            width: '38%',
            height: '32%',
            borderRadius: '50%',
            background:
              'radial-gradient(ellipse, oklch(98% 0.02 70 / 0.7), transparent 70%)',
            filter: 'blur(4px)',
          }}
        />
      </div>

      {/* Thinking dots */}
      {thinking && (
        <div style={{ position: 'absolute', bottom: -40, display: 'flex', gap: 6 }}>
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: T.terra,
                animation: `orbDot 1.2s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
